import _ from 'lodash';
import { history } from 'umi';

import { apiRoute, getAll } from '@/services/api';
import { sortFn } from '@/utils/infrastructure';
import { getToken } from '@/utils/infrastructure/communication/request';
import { CoreEnums } from '@/utils/domain/constants';
import { menuIcons, routing } from './utils';

const initialState = {
  portal: null,
  menuIcons,
  retryTimes: 0,
  token: getToken({ global: true }),
};

const Model = {
  namespace: 'companyPortal',
  state: initialState,
  effects: {
    *auth({ payload }, { call, put, select }) {
      try {
        const config = yield call(apiRoute, {
          method: 'post',
          route: 'auth/portal',
          data: {
            password: payload.portalCode,
            username: payload.companyCode,
          },
        });
        yield put({ type: 'queryAuth', payload: { ...config, ...payload } });
      } catch (e) {
        const retryTimes = yield select((o) => o.companyPortal.retryTimes);
        if (!retryTimes || retryTimes < 3) {
          yield put({ type: 'queryRetry', payload: 1 });
        }
      }
    },

    *menu(o, { call, put }) {
      try {
        const res = yield call(getAll, { route: 'processPortal/menu' });
        yield put({ type: 'queryMenu', payload: res });

        // Auto-navigate to default embed page after menu loads
        const hasNavigated = sessionStorage.getItem('x-portal-embed-navigated');
        const logoutInProgress = localStorage.getItem('x-logout-in-progress');

        if (!hasNavigated && !logoutInProgress && res?.length > 0) {
          // Find top-level items with default embed link
          const topLevelItems = res.reduce((acc, section) => {
            if (section.menuItems) {
              return [...acc, ...section.menuItems.filter((item) => !item.parent)];
            }
            return acc;
          }, []);

          const defaultEmbedItem = topLevelItems.find(
            (item) => item.default === true && item.embedHtmlLink,
          );

          if (defaultEmbedItem) {
            sessionStorage.setItem('x-portal-embed-navigated', 'true');
            history.replace({
              pathname: '/embed',
              query: { templateURL: encodeURIComponent(defaultEmbedItem.embedHtmlLink) },
            });
          }
        }
      } catch (e) {}
    },

    *logout({ payload }, { call, put, select }) {
      try {
        yield call(apiRoute, { route: 'auth/logout', method: 'post' });

        sessionStorage.removeItem('x-portal-embed-navigated');
        // Only remove auth token if this is a full logout, not just switching portals
        if (!payload.auto) localStorage.removeItem('x-auth-token');

        localStorage.removeItem('antd-pro-authority');
        localStorage.removeItem('x-auth-user');
        localStorage.removeItem('x-userContext');

        if (payload.isFailedLoginRedirect) {
          const portal = yield select((o) => o.companyPortal.portal);
          const loginUrl = portal ? `/user/login?portal=${portal.id}` : '/user/login';
          history.replace(loginUrl, portal || history.location.state);
          return;
        }

        if (payload.auto) {
          localStorage.removeItem('x-auth-companyPortalConfig');
          yield put({ type: 'reset' });
        } else yield put({ type: 'companyPortal/auth', payload });
      } catch (e) {
        const retryTimes = yield select((o) => o.companyPortal.retryTimes);
        if (!retryTimes || retryTimes < 3) {
          yield put({ type: 'queryRetry', payload: 1 });
        }
      }
    },

    *config({ payload }, { put }) {
      yield put({ type: 'queryConfig', payload });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'reset', payload });
    },
  },
  reducers: {
    queryAuth(state, { payload }) {
      const { portal, companyCode, portalCode, auto } = payload;
      const config = { ...portal, companyCode, portalCode };

      localStorage.setItem('x-auth-companyPortalConfig', JSON.stringify(config));
      sessionStorage.removeItem('x-portal-embed-navigated');

      // Check if user is in 2FA flow
      const twoFactorState = localStorage.getItem('x-auth-twoFactorState');
      const isInTwoFactorFlow = twoFactorState ? JSON.parse(twoFactorState).required : false;

      if (
        !auto &&
        !localStorage.getItem('x-logout-in-progress') &&
        !isInTwoFactorFlow &&
        !state.retryTimes
      ) {
        history.replace({ pathname: '/' }, config);
      }

      return {
        ...state,
        retryTimes: null,
        portal: config,
      };
    },

    queryMenu(state, { payload }) {
      function menuItem(item) {
        const { path, id, ...more } = routing[item.type] || {};

        const hasChildren = item.subItems && item.subItems.length > 0;
        const orderedChildren = item.subItems?.sort(sortFn);
        const key = _.camelCase(item.title);

        const fragments = id
          ? _(id)
              .split(',')
              .map((k) => item[k])
              .join('/')
          : '';

        return {
          ...more,
          name: item.title,
          key: _.camelCase(item.title),
          path: hasChildren ? `/${key}` : (path || item.link) + fragments,
          children: _.map(orderedChildren, menuItem),
          type: item.type === CoreEnums.PortalMenuItemType.Link ? 'link' : item.type,
          embedHtmlLink: item.embedHtmlLink,
        };
      }

      const orderedItems = payload?.sort(sortFn)?.reduce((lhs, rhs) => {
        return _.concat(
          lhs,
          [
            rhs.menuItems && {
              type: 'session',
              title: rhs.title,
              icon: null,
            },
            ...(rhs.menuItems?.sort(sortFn) || []),
          ].filter(Boolean),
        );
      }, []);

      const menu = _.map(orderedItems, menuItem);

      try {
        const stored = localStorage.getItem('x-auth-companyPortalConfig');
        localStorage.setItem(
          'x-auth-companyPortalConfig',
          JSON.stringify({ ...JSON.parse(stored), menu }),
        );
      } catch (e) {}

      return {
        ...state,
        portal: {
          ...state.portal,
          menu,
        },
      };
    },

    queryConfig(state, { payload }) {
      return {
        ...state,
        portal: payload,
      };
    },

    queryRetry(state, { payload }) {
      return {
        ...state,
        retryTimes: state.retryTimes + payload,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
